'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GeneralNotFound, Spinner, TaskViewer } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getEventTask, getEventDocuments } from 'src/api/personal/eventDetail';

export const Route = createFileRoute('/personal_/events/$eventId/tasks/$taskId')({
	component: TaskDetailPage,
});

function TaskDetailPage() {
	const { eventId, taskId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);
	const asOwner = !hasAllEventsView;

	const { data: task, isLoading: taskLoading } = useQuery({
		queryKey: ['personal-event-task', eventId, taskId],
		queryFn: () => getEventTask(eventId, taskId),
	});

	const { data: documentsData, isLoading: documentsLoading } = useQuery({
		queryKey: ['personal-event-documents', eventId, asOwner],
		queryFn: () => getEventDocuments(eventId, asOwner),
		enabled: !!task,
	});

	const handleClose = () => {
		navigate({ to: '/personal/events/$eventId/tasks', params: { eventId } });
	};

	const handleOpenDocument = (documentId: string) => {
		window.open(`/personal/events/${eventId}/documents/${documentId}`, '_blank');
	};

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-event-task', eventId, taskId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event-tasks', eventId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event', eventId] });
	};

	const isLoading = authLoading || taskLoading || documentsLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!task) {
		return (
			<GeneralNotFound
				label="We couldn't find this task. Maybe it has been deleted?"
				action={{
					label: 'Return to tasks',
					href: `/personal/events/${eventId}/tasks`,
					variant: 'outlined',
				}}
			/>
		);
	}

	const documents = (documentsData?.data || []).filter((d: { _id: string }) =>
		task.documentIds?.includes(d._id),
	);

	return (
		<TaskViewer
			task={task}
			documents={documents}
			onUpdate={handleUpdate}
			onClose={handleClose}
			onOpenDocument={handleOpenDocument}
			permissions={{
				taskDelete: hasAllEventsView || asOwner,
				taskDo: hasAllEventsView || asOwner,
			}}
		/>
	);
}
